import { useDispatch, useSelector } from 'react-redux';
import { Button, IcoArrowUpDown, IcoFailedLoad, Modal, Select } from '..';
import { modalOpen } from '../../modules/dashboard/redux/dashboardActions';
import React, { useEffect, useState } from 'react';
import './charts.scss';
import { loaded, loading } from '../../layout/redux/layoutActions';
import { DataTypes, IChartProps } from '.';
import { exportToPNG } from '../../shared/utils/exporter';

export interface IChartCardProps {
  id: string;
  cy: string;
  Chart: React.FC<IChartProps<any>>;
  hasAnnualDataOnly?: boolean;
  requestStatus: string;
  data: Array<DataTypes & { type?: string }>;
  data_name?: string;
  extraClasses?: string;
}

const ChartCard: React.FC<IChartCardProps> = ({
  Chart,
  id,
  cy,
  hasAnnualDataOnly = false,
  requestStatus,
  data,
  extraClasses,
}) => {
  const dispatch = useDispatch();
  const { preference } = useSelector((state: any) => state.layoutState);
  const nls = require(`./nls/${preference.language.id}.json`);
  const [newData, setNewData] = useState<any>(null);

  useEffect(() => {
    if (data && requestStatus === 'LOADED') {
      if (data[0]?.type) {
        setNewData(data?.filter((item: any) => item?.type === 'year'));
      } else {
        setNewData(data);
      }
    }
  }, [requestStatus]);

  return (
    <div data-cy={`${cy}Container`} className="chart-card--full-container">
      <div className="chart-card--full-container--header-container">
        {data && requestStatus === 'LOADED' && (
          <>
            {nls[id]}
            <Button
              cy={`OpenModal[${cy}]`}
              onClick={() => dispatch(modalOpen(`open ${id}`))}
              className="btn secondary"
              style={{
                boxShadow: 'none',
                backgroundColor: '#FFF',
                border: 'none',
              }}
            >
              <IcoArrowUpDown cy={`Chart${cy}`} />
            </Button>
          </>
        )}
        {data && requestStatus === 'LOADING' && (
          <div className="placeholder"></div>
        )}
        {data && requestStatus === 'FAILED' && <>{nls[id]}</>}
      </div>
      <div
        className={`chart-card--full-container--component-container ${extraClasses}`}
      >
        {requestStatus === 'LOADED' && newData && (
          <Chart
            data={newData}
            cy={`ChartCard${cy}`}
            id={`${id}Chart`}
            timeframe="Ano"
          />
        )}
        {data && requestStatus === 'LOADING' && (
          <div className="placeholder"></div>
        )}
        {data && requestStatus === 'FAILED' && (
          <div className="failed">
            <IcoFailedLoad cy={cy} />
          </div>
        )}
      </div>
      {data && requestStatus === 'LOADED' && newData && (
        <ChartModal
          hasAnnualDataOnly={hasAnnualDataOnly}
          id={id}
          cy={cy}
          Chart={Chart}
          requestStatus={requestStatus}
          data={data}
        />
      )}
    </div>
  );
};

const ChartModal: React.FC<IChartCardProps> = ({
  id,
  cy,
  Chart,
  hasAnnualDataOnly,
  requestStatus,
  data,
}: IChartCardProps) => {
  const dispatch = useDispatch();
  const { preference } = useSelector((state: any) => state.layoutState);
  const { statusModal } = useSelector((state: any) => state.dashboardState);
  const nls = require(`./nls/${preference.language.id}.json`);
  const [newData, setNewData] = useState<any>(
    hasAnnualDataOnly
      ? data?.filter((el: any) => el.type === 'year')
      : data?.filter((el: any) => el.type === 'month')
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState('Ano');
  const SELECT_OPTIONS = [
    { name: 'Mês', id: 'month' },
    { name: 'Trimestre', id: 'quarter' },
    { name: 'Ano', id: 'year' },
  ];
  useEffect(() => {
    if (data && requestStatus === 'LOADED') {
      if (data[0]?.type) {
        setNewData(data?.filter((el: any) => el?.type === 'year'));
      } else {
        setNewData(data);
      }
    }
  }, [requestStatus]);
  const handleSelect = (e: any) => {
    if (e.value.name !== undefined) {
      setSelectedTimeframe(e.value.name);
      setNewData(data?.filter((el: any) => el.type === e.value.id));
    } else {
      setSelectedTimeframe('');
    }
  };
  const modalSize = 'large-full';
  const modalId = `Modal${modalSize}Chart${cy}FullContainer`;
  const saveAsPNG = async () => {
    if (window.Cypress) {
      window.saveAsPNG = saveAsPNG;
    }
    const intlName = nls[id] ? nls[id] : id;
    dispatch(loading());
    await exportToPNG(modalId, intlName);
    setTimeout(() => {
      dispatch(loaded());
    }, 700);
  };

  return (
    <Modal
      open={statusModal === `open ${id}`}
      title={nls[id]}
      close={() => dispatch(modalOpen(''))}
      cy={`Chart${cy}`}
      size={modalSize}
      actions={
        <>
          <Button
            className="chart-modal-save-png-button btn"
            cy="SaveAsPNG"
            color="secondary"
            variant="outline"
            onClick={saveAsPNG}
          >
            {nls.savePNG}
          </Button>
        </>
      }
    >
      <div className="chart-modal--full-container" id={modalId}>
        <div className="chart-modal--content-container">
          <div className="chart-modal--checkbox-container">
            {!hasAnnualDataOnly && (
              <div className="chart-modal--checkbox-container--filters-container">
                <Select
                  label=""
                  name="selectedTimeframe"
                  selectedItem={false}
                  options={SELECT_OPTIONS}
                  cy={cy}
                  selected={selectedTimeframe || 'Mês'}
                  action={handleSelect}
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>
          <div
            className="chart-modal--chart-container"
            data-cy={`ChartModal${cy}ChartContainer`}
          >
            <Chart
              data={newData}
              cy={cy}
              id={`${id}ChartModal`}
              timeframe={selectedTimeframe || 'Mês'}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(ChartCard);
